import React from "react";
import { EnumAlertDialogType, UserModel } from "../Managers/dataStructures";
import { ActivityIndicator, Toast } from "../Managers/utilityComponents";
import {
  delayCall,
  fetchChangePassword,
  fetchChangePasswordAdmin,
} from "../Managers/wepApiManager";
import bcrypt from "bcryptjs";
import { getEncryptionKey } from "../Managers/profileManager";
import { getLanguage } from "../Managers/dataProvider";

interface ChangePasswordProps {
  isAdmin: boolean;
  user: UserModel;
  closeDialog: () => void;
}

export default function ChangePasswordForm(props: ChangePasswordProps) {
  const isEnglish = getLanguage();
  const [toastType, setToastType] = React.useState<EnumAlertDialogType>(
    EnumAlertDialogType.NEUTRAL
  );
  const [toastTitle, setToastTitle] = React.useState<string>("");
  const [isActivityProgress, setIsActivityProgress] =
    React.useState<boolean>(false);
  let [oldPassword, setOldPassword] = React.useState<string>();
  let [password, setPassword] = React.useState<string>();
  let [confirmPassword, setConfirmPassword] = React.useState<string>();

  const btnTopMargin =
    toastType === EnumAlertDialogType.NEUTRAL ? "mt-10" : "mt-2";

  function onCancel() {
    console.log("TLLoginDialog - onCancel");
    props.closeDialog();
  }

  function onProceed() {
    if (isActivityProgress) {
      return;
    }

    if (props.isAdmin === false) {
      if (oldPassword === undefined) {
        showToast(
          isEnglish
            ? "Please Enter Current Password"
            : "Bitte geben Sie das aktuelle Passwort ein",
          false
        );
        return;
      }
    }

    if (password === undefined || confirmPassword === undefined) {
      showToast(
        isEnglish ? "Please Enter New Password" : "Neues Passwort eingeben",
        false
      );
      return;
    }

    if (password !== confirmPassword) {
      showToast(
        isEnglish
          ? "Passwords don't match."
          : "Passwörter stimmen nicht überein.",
        false
      );
      return;
    }

    setIsActivityProgress(true);
    bcrypt.hash(
      password,
      getEncryptionKey(),
      (herr: Error | null, hash: string) => {
        if (herr == null) {
          if (props.isAdmin === false) {
            bcrypt.hash(
              oldPassword as string,
              getEncryptionKey(),
              (oldherr: Error | null, oldHash: string) => {
                if (oldherr == null) {
                  fetchChangePassword(
                    props.user.email_id,
                    oldHash,
                    hash,
                    (isSuccess: boolean, message: string) => {
                      if (isSuccess) {
                        showToast(
                          isEnglish
                            ? "Password Successfully Changed"
                            : "Passwort erfolgreich geändert." + message,
                          true
                        );
                        delayCall(2000, () => {
                          setIsActivityProgress(false);
                          props.closeDialog();
                        });
                      } else {
                        showToast(
                          isEnglish
                            ? "Failed to change password"
                            : "Fehler beim Ändern des Passworts.",
                          false
                        );
                        delayCall(2000, () => {
                          setIsActivityProgress(false);
                        });
                      }
                    }
                  );
                } else {
                  showToast(
                    isEnglish
                      ? "Failed to change password"
                      : "Fehler beim Ändern des Passworts.",
                    false
                  );
                  delayCall(2000, () => {
                    setIsActivityProgress(false);
                  });
                }
              }
            );
          } else {
            fetchChangePasswordAdmin(
              props.user.email_id,
              hash,
              (isSuccess: boolean, message: string) => {
                if (isSuccess) {
                  showToast(
                    (isEnglish
                      ? "Password Successfully Changed"
                      : "Passwort erfolgreich geändert.") + message,
                    true
                  );
                } else {
                  showToast(
                    isEnglish
                      ? "Failed to change password"
                      : "Fehler beim Ändern des Passworts.",
                    false
                  );
                }
                delayCall(2000, () => {
                  setIsActivityProgress(false);
                  props.closeDialog();
                });
              }
            );
          }
        } else {
          console.log("hashError=" + herr);
        }
      }
    );
  }

  function showToast(title: string, isPositive: boolean) {
    setToastTitle(title);
    setToastType(
      isPositive ? EnumAlertDialogType.POSITIVE : EnumAlertDialogType.NEGATIVE
    );
    delayCall(1500, () => {
      setToastType(EnumAlertDialogType.NEUTRAL);
      setToastTitle("");
    });
  }

  return (
    <div className="bg-white shadow sm:rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div>
          <h3 className="text-base font-semibold leading-6 text-gray-900 w-max mx-auto">
            {props.user.email_id}
          </h3>
          <div className="mt-2 max-w-xl text-sm mx-auto w-max text-gray-500">
            <p>{isEnglish ? "Please enter password to proceed." : ""}</p>
          </div>
          {props.isAdmin ? null : (
            <div className="mt-5 sm:flex sm:items-center">
              <div className="w-full sm:max-w-xs mx-auto">
                <label htmlFor="email" className="sr-only">
                  {isEnglish ? "Password" : "Passwort"}
                </label>
                <input
                  type="password"
                  name="oldpassword"
                  id="oldpassword"
                  autoComplete="password"
                  onChange={(x) => setOldPassword(x.currentTarget.value)}
                  className="block w-full font-semibold rounded-md border-0 py-1.5 px-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  placeholder="Enter Old Password"
                  disabled={isActivityProgress}
                />
              </div>
            </div>
          )}
          <div className="mt-10 sm:flex sm:items-center">
            <div className="w-full sm:max-w-xs mx-auto">
              <label htmlFor="email" className="sr-only">
                {isEnglish ? "Password" : "Passwort"}
              </label>
              <input
                type="password"
                name="password"
                id="password"
                autoComplete="password"
                onChange={(x) => setPassword(x.currentTarget.value)}
                className="block w-full font-semibold rounded-md border-0 py-1.5 px-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                placeholder={
                  isEnglish ? "Enter New Password" : "Neues Passwort eingeben"
                }
                disabled={isActivityProgress}
              />
            </div>
          </div>
          <div className="mt-5 sm:flex sm:items-center">
            <div className="w-full sm:max-w-xs mx-auto">
              <label htmlFor="email" className="sr-only">
                {isEnglish ? "Confirm Password" : "Neues Passwort bestätigen"}
              </label>
              <input
                type="password"
                name="confirmpassword"
                id="confirmpassword"
                autoComplete="password"
                onChange={(x) => setConfirmPassword(x.currentTarget.value)}
                className="block w-full font-semibold rounded-md border-0 py-1.5 px-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                placeholder={
                  isEnglish
                    ? "Confirm New Password"
                    : "Neues Passwort bestätigen"
                }
                disabled={isActivityProgress}
              />
            </div>
          </div>

          {toastType === EnumAlertDialogType.NEUTRAL ? null : (
            <div className="mt-2">{Toast(toastTitle, toastType)} </div>
          )}
          <div
            className={"sm:flex sm:items-center justify-center " + btnTopMargin}
          >
            <button
              type="submit"
              onClick={onProceed}
              className="inline-flex w-full items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:ml-3 sm:mt-0 sm:w-auto"
            >
              {isActivityProgress ? ActivityIndicator(0) : null}
              {isEnglish ? "Change Password" : "Passwortändern"}
            </button>
            <button
              onClick={onCancel}
              type="button"
              className="text-sm font-semibold w-full leading-6 text-gray-900 sm:ml-5 sm:mt-0 sm:w-auto"
            >
              {isEnglish ? "Cancel" : "Abbrechen"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/*
export default function ChangePassDialog(user: UserModel) {
  let [password_] = React.useState<string>();
  let [confirmPassword_] = React.useState<string>();

  function onPassword(value: string) {
    password_ = value;
  }

  function onPasswordConfirm(value: string) {
    confirmPassword_ = value;
  }

  function onCancel() {
    console.log("TLLoginDialog - onCancel");
    //props.closeDialog();
  }

  function onProceed() {}

  return (
    <div
      className="relative z-50"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        aria-hidden="true"
      ></div>

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <div
            className={
              "relative transform overflow-hidden rounded-lg px-4 pb-4 pt-5 transition-all sm:my-8 sm:p-6 lg:w-2/5 w-4/5"
            }
          >
            {ChangePasswordForm(
              user,
              onPassword,
              onPasswordConfirm,
              onCancel,
              onProceed
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
*/
